import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  Activity,
  ChevronDown,
  ChevronUp,
  Pencil,
  Settings,
  Shield,
  ShieldCheck,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

export function RoutesTable({
  routes,
  onToggleStatus,
  onEditAccess,
  onEditSettings,
  onEdit,
  isUpdating,
  disableAll,
}: {
  routes: Array<{
    id: string
    name: string
    path: string
    enabled: boolean
    modules?: string[]
    /** Route component identifier (e.g. "free" for AI Explorer). The Deep
     * Explorer backend-overrides Settings button only renders on routes
     * whose component is "free"; on every other route those settings have
     * no effect and the affordance would be misleading. */
    component?: string
    access_configured?: boolean
  }>
  onToggleStatus: (routeId: string, enabled: boolean) => void
  onEditAccess?: (routeId: string) => void
  onEditSettings?: (routeId: string) => void
  /** Open the Form+Raw edit dialog for this route (backend overrides). */
  onEdit?: (routeId: string) => void
  isUpdating?: string
  disableAll?: boolean
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  // Sort routes by name (case-insensitive)
  const sortedRoutes = useMemo(() => {
    if (routes.length === 0) return []
    return [...routes].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }, [routes])

  const controls = useAdminTableControls(sortedRoutes, {
    getName: (row) => row.name,
    getSearchText: (row) => [row.name, row.id, row.path].join(" "),
    isEnabled: (row) => row.enabled,
  })
  const displayRoutes = isExpanded ? controls.rows : controls.rows.slice(0, 5)
  const hasMoreRoutes = controls.count > 5

  if (routes.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.routes.emptyDescription", "No routes configured")}
        icon={Activity}
        title={t("adminDashboard.routes.emptyTitle", "No Routes")}
      />
    )
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar bulkActions controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.routes.name", "Name")}</div>
        <div className="col-span-3">{t("adminDashboard.routes.path", "Path")}</div>
        <div className="col-span-2">{t("adminDashboard.routes.modules", "Modules")}</div>
        <div className="col-span-1 text-right">
          {t("adminDashboard.routes.settings", { defaultValue: "Settings" })}
        </div>
        <div className="col-span-1 text-right">
          {t("adminDashboard.access.access", { defaultValue: "Access" })}
        </div>
        <div className="col-span-2 text-right">{t("adminDashboard.routes.status", "Status")}</div>
      </div>

      {displayRoutes.map((route) => (
        <div
          key={route.id}
          className="admin-responsive-grid-row bg-card hover:bg-accent/50 grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
        >
          <div className="col-span-3 flex flex-col">
            <span className="font-medium">{route.name}</span>
            <span className="text-muted-foreground text-xs">{route.id}</span>
          </div>
          <div className="text-muted-foreground col-span-3 flex items-center gap-3 font-mono text-xs lg:block">
            <span className="text-foreground w-24 shrink-0 font-sans font-medium lg:hidden">
              Path
            </span>
            <span>{route.path}</span>
          </div>
          <div className="text-muted-foreground col-span-2 flex items-start gap-3 text-xs lg:block">
            <span className="text-foreground w-24 shrink-0 font-medium lg:hidden">Modules</span>
            <span>
              {route.modules && route.modules.length > 0 ? route.modules.join(", ") : "—"}
            </span>
          </div>
          <div className="col-span-1 flex items-center justify-end gap-1">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Actions
            </span>
            {onEdit ? (
              <Button
                disabled={disableAll}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.routes.editAriaLabel", {
                  defaultValue: "Edit route overrides",
                })}
                onClick={() => onEdit(route.id)}
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
            {/* Deep Explorer-only: the backend overrides (max parallel
                subagents, max iterations, smart followups, custom
                instructions) are runtime knobs for the Explorer agent and
                have no effect on other route components (e.g. tender,
                ai-portfolio). Surfacing the Settings affordance on those
                routes is misleading — hide it. */}
            {route.component === "free" ? (
              <Button
                aria-label={t("adminDashboard.routes.settings", { defaultValue: "Settings" })}
                disabled={disableAll || !onEditSettings}
                size="icon"
                type="button"
                variant="outline"
                onClick={() => onEditSettings?.(route.id)}
              >
                <Settings className="size-4" />
              </Button>
            ) : null}
          </div>
          <div className="col-span-1 flex items-center justify-end">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Access
            </span>
            <Button
              aria-label={t("adminDashboard.access.access", { defaultValue: "Access" })}
              disabled={disableAll || !onEditAccess}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => onEditAccess?.(route.id)}
            >
              {route.access_configured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
            </Button>
          </div>
          <div className="col-span-2 flex items-center justify-end">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Status
            </span>
            <Switch
              aria-label={`${route.name} status`}
              checked={route.enabled}
              disabled={disableAll || isUpdating === route.id}
              onCheckedChange={(checked) => onToggleStatus(route.id, checked)}
            />
            <span className="text-muted-foreground ml-2 text-xs">
              {route.enabled
                ? t("adminDashboard.routes.enabled", "Enabled")
                : t("adminDashboard.routes.disabled", "Disabled")}
            </span>
          </div>
        </div>
      ))}

      {hasMoreRoutes && (
        <div className="flex justify-center pt-2">
          <Button
            className="text-muted-foreground hover:text-foreground w-full justify-center text-sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.routes.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.routes.showMore", "Show More", {
                  count: controls.count - 5,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
